/**
 * PressPdfStatusChip — reusable status indicator + regen trigger for the
 * 112 print-ready BLM PDFs. Polls GET /api/content-auditor/regen-press-status
 * every 15s. Shows "Press PDFs: N/112" with color-coded state:
 *   - emerald when N === expected (all good)
 *   - amber when N < expected (stale or in-progress)
 *   - indigo Loader2 when running, with done/total live counter
 *
 * v6.32.31: Re-enabled regen click after the GridFS storage migration.
 * The chip now reads from `progress` (a per-PDF MongoDB doc) so the
 * counter advances in real time, not after a 4-KB stdout buffer flush.
 * Duplicate-job guard at the backend prevents two concurrent regens.
 *
 * Used by BPTextFixBatchPanel.js and BPVisualFixBatchPanel.js headers.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, FileCheck2, AlertTriangle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const PressPdfStatusChip = ({ pollIntervalMs = 15000 }) => {
  const [status, setStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/content-auditor/regen-press-status`,
        authCfg(),
      );
      setStatus(data);
    } catch (e) {
      // Silent — chip just won't update
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, pollIntervalMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchStatus, pollIntervalMs]);

  const triggerRegen = async () => {
    const pending = status?.pdf_count || 0;
    const expected = status?.expected_count || 112;
    const msg = pending < expected
      ? `Only ${pending}/${expected} BLM PDFs exist in GridFS. Re-trigger press-PDF regeneration now? (~6 minutes, stored cross-pod in MongoDB)`
      : `All ${expected}/${expected} PDFs are fresh in GridFS. Re-trigger anyway?`;
    if (!window.confirm(msg)) return;
    setTriggering(true);
    try {
      await axios.post(`${API}/content-auditor/regen-press-pdfs`, {}, authCfg());
      toast.success('Press-PDF regen started. Live progress updates every 15 s.');
      setTimeout(fetchStatus, 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === 'regen_already_running') {
        toast.error(
          `Already running: ${detail.done}/${detail.total} done by ${detail.started_by} `
          + `(${detail.age_sec}s ago). Wait for it to finish or use the cancel control.`,
        );
      } else {
        toast.error(`Regen trigger failed: ${typeof detail === 'string' ? detail : (detail?.message || e.message)}`);
      }
    } finally {
      setTriggering(false);
    }
  };

  // v6.32.133 — Cancel-stuck handler. Wired to /regen-press/cancel-stuck.
  // Surfaces the diagnostics summary first so the operator knows what
  // they're cancelling.
  const cancelStuck = async () => {
    setCancelling(true);
    try {
      const diag = await axios.get(`${API}/content-auditor/regen-press/diagnostics`, authCfg());
      const lock = diag?.data?.active_lock || {};
      const overflow = diag?.data?.gridfs_overflow_revisions;
      const dupes = (diag?.data?.gridfs_overflow_dupes || []).length;
      const summary =
        `Active lock: ${lock.status || 'none'}\n` +
        `Heartbeat age: ${lock.heartbeat_age_sec ?? '—'}s${lock.heartbeat_stale ? ' (STALE)' : ''}\n` +
        `Progress: ${lock.done ?? 0}/${lock.total ?? 0}\n` +
        `GridFS overflow: ${overflow == null ? '—' : overflow + ' extra revisions'}\n` +
        `Duplicate filenames: ${dupes}\n\n` +
        `Clear the press_regen lock so a new regen can start?`;
      // eslint-disable-next-line no-alert
      if (!window.confirm(summary)) return;
      const { data } = await axios.post(
        `${API}/content-auditor/regen-press/cancel-stuck`,
        { confirm: true, reason: 'Operator cancel via chip' },
        authCfg(),
      );
      if (data.cleared) {
        toast.success(`Lock cleared (previous: ${data.previous_status}, age ${data.previous_age_sec}s).`);
      } else {
        toast.info(data.message || 'Nothing to clear.');
      }
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      toast.error(`Cancel failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCancelling(false);
    }
  };

  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 px-2 py-1">
        <Loader2 size={11} className="animate-spin" /> Press PDFs…
      </span>
    );
  }

  const pdfCount = status.pdf_count || 0;
  const expected = status.expected_count || 112;
  const running = status.running;
  const progressStale = status.progress_stale;
  const short = pdfCount < expected;
  const progress = status.progress || {};
  const done = progress.done || 0;
  const failCount = progress.fail || 0;

  const color = running
    ? 'border-indigo-300 text-indigo-700 bg-indigo-50'
    : progressStale
      ? 'border-rose-300 text-rose-700 bg-rose-50'
      : short
        ? 'border-amber-300 text-amber-800 bg-amber-50'
        : 'border-emerald-300 text-emerald-800 bg-emerald-50';

  const liveCounter = running ? `${done}/${expected}` : `${pdfCount}/${expected}`;

  const titleText = running
    ? `Regen in progress — ${done}/${expected} done${failCount ? `, ${failCount} fail` : ''}. Stored cross-pod in MongoDB GridFS. Click to (force) re-trigger.`
    : progressStale
      ? `Last regen attempt stalled (>5 min since heartbeat). Click to start fresh.`
      : short
        ? `${expected - pdfCount} BLM PDFs missing in GridFS — click to regenerate`
        : `All ${expected}/${expected} PDFs in GridFS — click to re-run regen`;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={triggerRegen}
      disabled={triggering}
      className={`${color} hover:opacity-90`}
      data-testid="press-pdf-status-chip"
      title={titleText}
    >
      {running
        ? <Loader2 size={14} className="mr-1 animate-spin" />
        : progressStale
          ? <AlertTriangle size={14} className="mr-1" />
          : short
            ? <AlertTriangle size={14} className="mr-1" />
            : <FileCheck2 size={14} className="mr-1" />}
      {triggering
        ? <Loader2 size={12} className="mr-1 animate-spin" />
        : null}
      Press PDFs: {liveCounter}
      {running && (
        <span className="ml-1 text-[10px]" data-testid="press-pdf-running-badge">
          (running{failCount ? `, ${failCount} fail` : ''})
        </span>
      )}
      {progressStale && (
        <span
          className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5
                     bg-rose-100 border border-rose-200 rounded text-rose-700"
          data-testid="press-pdf-stale-badge"
        >
          stalled
        </span>
      )}
      {!running && !progressStale && short && (
        <RefreshCw size={11} className="ml-1" />
      )}
      <span
        className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5
                   bg-slate-100 border border-slate-200 rounded text-slate-600"
        data-testid="press-pdf-storage-badge"
        title="Stored in MongoDB GridFS — multi-pod safe"
      >
        gridfs
      </span>
      {/* v6.32.133 — Cancel-stuck escape hatch. Only renders when the lock is
          stalled (heartbeat > 5 min) so it doesn't tempt operators to cancel
          a healthy in-progress run. */}
      {progressStale && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); if (!cancelling) cancelStuck(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (!cancelling) cancelStuck(); } }}
          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-semibold rounded cursor-pointer disabled:opacity-40"
          data-testid="press-pdf-cancel-stuck-btn"
          title="Clear the stale press_regen lock so a new regen can start. Shows diagnostics summary first."
        >
          {cancelling ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
          Cancel Stuck
        </span>
      )}
    </Button>
  );
};

export default PressPdfStatusChip;
